import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  IconButton,
  Typography,
  Grid,
  Select,
  MenuItem,
  Paper,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  MoneyTransactionFragmentFragment,
  CreateExpenseDocument,
  CreateExpenseMutation,
  CreateExpenseMutationVariables,
  SetExpenseUnitAssignmentsDocument,
  SetExpenseUnitAssignmentsMutation,
  SetExpenseUnitAssignmentsMutationVariables,
  CreateExpensePaymentDocument,
  CreateExpensePaymentMutation,
  CreateExpensePaymentMutationVariables,
  GetUnitsDocument,
  GetUnitsQuery,
  GetUnitsQueryVariables,
} from '../../../generated/graphql';
import { useAlert } from '@dwelvio/react-alert';
import { useApp } from '../../../components/app-context';
import { AddIcon, RemoveItemIcon } from '../../../components/icons';

interface UnitAssignmentRow {
  unitId: string;
  amount: string;
}

interface CreateExpenseFromTransactionDialogProps {
  open: boolean;
  transaction: MoneyTransactionFragmentFragment | null;
  onClose: () => void;
  onCreated: () => void;
}

export function CreateExpenseFromTransactionDialog({
  open,
  transaction,
  onClose,
  onCreated,
}: CreateExpenseFromTransactionDialogProps) {
  const { show } = useAlert();
  const app = useApp();

  const [title, setTitle] = useState('');
  const [amount, setAmount] = useState('');
  const [taxDeductible, setTaxDeductible] = useState('');
  const [taxDeductibleEdited, setTaxDeductibleEdited] = useState(false);
  const [date, setDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [refNo, setRefNo] = useState('');
  const [titleError, setTitleError] = useState(false);
  const [assignmentRows, setAssignmentRows] = useState<UnitAssignmentRow[]>([]);

  const { data: unitsData } = useQuery<GetUnitsQuery, GetUnitsQueryVariables>(GetUnitsDocument, {
    variables: { includeArchived: false },
  });
  const availableUnits = unitsData?.getUnits?.filter((u) => u.status !== 'archived') ?? [];

  const [createExpense, { loading: createLoading }] = useMutation<
    CreateExpenseMutation,
    CreateExpenseMutationVariables
  >(CreateExpenseDocument);

  const [setUnitAssignments, { loading: assignmentsLoading }] = useMutation<
    SetExpenseUnitAssignmentsMutation,
    SetExpenseUnitAssignmentsMutationVariables
  >(SetExpenseUnitAssignmentsDocument);

  const [createPayment, { loading: paymentLoading }] = useMutation<
    CreateExpensePaymentMutation,
    CreateExpensePaymentMutationVariables
  >(CreateExpensePaymentDocument);

  useEffect(() => {
    if (open && transaction) {
      const todayStr = new Date().toISOString().substring(0, 10);
      const availableAmount = transaction.value - transaction.reconciledValue;
      setTitle('');
      setAmount(availableAmount.toFixed(2));
      setTaxDeductible(availableAmount.toFixed(2));
      setTaxDeductibleEdited(false);
      setDate(
        transaction.transactionDate ? new Date(transaction.transactionDate).toISOString().substring(0, 10) : todayStr,
      );
      setDueDate(todayStr);
      setRefNo(transaction.reference ?? '');
      setTitleError(false);
      setAssignmentRows([]);
    }
  }, [open, transaction]);

  const parsedAmount = parseFloat(amount) || 0;
  const parsedTaxDeductible = parseFloat(taxDeductible);
  const effectiveTaxDeductible = !isNaN(parsedTaxDeductible) ? parsedTaxDeductible : parsedAmount;

  const assignmentTotal = assignmentRows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
  const assignmentTotalMatchesExpense = assignmentRows.length === 0 || Math.abs(assignmentTotal - parsedAmount) < 0.001;

  const previewTaxDeductible = (idx: number): number => {
    const rowAmount = parseFloat(assignmentRows[idx]?.amount) || 0;
    if (parsedAmount <= 0 || rowAmount <= 0) return 0;
    const raw = (rowAmount / parsedAmount) * effectiveTaxDeductible;
    let td = Math.round(raw * 100) / 100;
    if (idx === 0) {
      const sumOthers = assignmentRows.slice(1).reduce((s, r) => {
        const a = parseFloat(r.amount) || 0;
        const rawOther = parsedAmount > 0 ? (a / parsedAmount) * effectiveTaxDeductible : 0;
        return s + Math.round(rawOther * 100) / 100;
      }, 0);
      const diff = Math.round((effectiveTaxDeductible - sumOthers - td) * 100) / 100;
      td = Math.round((td + diff) * 100) / 100;
    }
    return td;
  };

  const handleAddRow = () => setAssignmentRows((prev) => [...prev, { unitId: '', amount: '' }]);

  const handleRemoveRow = (idx: number) => setAssignmentRows((prev) => prev.filter((_, i) => i !== idx));

  const handleRowChange = (idx: number, field: 'unitId' | 'amount', value: string) =>
    setAssignmentRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));

  const handleSubmit = async () => {
    if (!transaction) return;

    if (!title.trim()) {
      setTitleError(true);
      return;
    }
    if (!parsedAmount || parsedAmount <= 0) {
      show.error('Zadajte platnú sumu.');
      return;
    }
    if (assignmentRows.length > 0 && !assignmentTotalMatchesExpense) {
      show.error(
        `Súčet priradených súm (${app.formatNumber(assignmentTotal, 2)} €) sa musí rovnať celkovej sume výdavku (${app.formatNumber(parsedAmount, 2)} €).`,
      );
      return;
    }

    // 1. Create expense
    const expenseResult = await createExpense({
      variables: {
        input: {
          title: title.trim(),
          amount: parsedAmount,
          taxDeductible: effectiveTaxDeductible,
          date: new Date(date).toISOString(),
          dueDate: new Date(dueDate).toISOString(),
          refNo: refNo.trim() || undefined,
          isCost: false,
        },
      },
    });

    if (expenseResult.data?.createExpense?.errors?.length) {
      show.error('Nepodarilo sa vytvoriť výdavok.', expenseResult.data.createExpense.errors[0].code);
      return;
    }

    const createdExpense = expenseResult.data?.createExpense?.expense;
    if (!createdExpense) {
      show.error('Nepodarilo sa vytvoriť výdavok.');
      return;
    }

    // 2. Set unit assignments (if any)
    const validRows = assignmentRows.filter((r) => r.unitId && parseFloat(r.amount) > 0);
    if (validRows.length > 0) {
      const assignResult = await setUnitAssignments({
        variables: {
          expenseId: createdExpense.id,
          assignments: validRows.map((r) => ({ unitId: r.unitId, amount: parseFloat(r.amount) })),
        },
      });
      if (assignResult.data?.setExpenseUnitAssignments?.errors?.length) {
        show.error(
          'Výdavok bol vytvorený, ale nepodarilo sa priradiť nehnuteľnosti.',
          assignResult.data.setExpenseUnitAssignments.errors[0].code,
        );
      }
    }

    // 3. Create expense payment linking the transaction
    const paymentResult = await createPayment({
      variables: {
        input: {
          transactionId: transaction.id,
          expenseId: createdExpense.id,
          amount: parsedAmount,
        },
      },
    });

    if (paymentResult.data?.createExpensePayment?.errors?.length) {
      const error = paymentResult.data.createExpensePayment.errors[0];
      show.error('Výdavok bol vytvorený, ale nepodarilo sa priradiť transakciu.', error.code);
      onCreated();
      return;
    }

    show.info('Výdavok bol úspešne vytvorený a priradený k transakcii.');
    onCreated();
  };

  const loading = createLoading || assignmentsLoading || paymentLoading;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          Vytvoriť výdavok
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Názov *"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setTitleError(!e.target.value.trim());
              }}
              error={titleError}
              helperText={titleError ? 'Názov je povinný' : ''}
              fullWidth
              variant="standard"
              disabled={loading}
              autoFocus
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Suma (€) *"
              type="number"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                if (!taxDeductibleEdited) setTaxDeductible(e.target.value);
              }}
              fullWidth
              variant="standard"
              disabled={loading}
              inputProps={{ step: '0.01', min: '0.01' }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Odpočítateľné z dane (€)"
              type="number"
              value={taxDeductible}
              onChange={(e) => {
                setTaxDeductible(e.target.value);
                setTaxDeductibleEdited(true);
              }}
              fullWidth
              variant="standard"
              disabled={loading}
              inputProps={{ step: '0.01', min: '0' }}
              helperText="Predvolene = celá suma"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Dátum výdavku *"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              fullWidth
              variant="standard"
              disabled={loading}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Dátum splatnosti"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              fullWidth
              variant="standard"
              disabled={loading}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Referenčné číslo"
              value={refNo}
              onChange={(e) => setRefNo(e.target.value)}
              fullWidth
              variant="standard"
              disabled={loading}
            />
          </Grid>

          {/* Unit assignments */}
          <Grid size={{ xs: 12 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2">Priradenie k nehnuteľnostiam</Typography>
              <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleAddRow} disabled={loading}>
                Pridať
              </Button>
            </Stack>

            {assignmentRows.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Výdavok nie je priradený k žiadnej nehnuteľnosti.
              </Typography>
            )}

            <Stack spacing={1}>
              {assignmentRows.map((row, idx) => (
                <Paper key={idx} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Select
                      value={row.unitId}
                      onChange={(e) => handleRowChange(idx, 'unitId', e.target.value)}
                      displayEmpty
                      size="small"
                      disabled={loading}
                      sx={{ flex: 1, minWidth: 120 }}
                    >
                      <MenuItem value="">— vybrať —</MenuItem>
                      {availableUnits.map((u) => (
                        <MenuItem key={u.id} value={u.id}>
                          {u.name}
                        </MenuItem>
                      ))}
                    </Select>
                    <TextField
                      value={row.amount}
                      onChange={(e) => handleRowChange(idx, 'amount', e.target.value)}
                      type="number"
                      size="small"
                      disabled={loading}
                      label="Suma (€)"
                      inputProps={{ step: '0.01', min: '0.01' }}
                      sx={{ width: 120 }}
                    />
                    <TextField
                      value={app.formatNumber(previewTaxDeductible(idx), 2)}
                      size="small"
                      disabled
                      label="Odp. z dane (€)"
                      sx={{ width: 130 }}
                    />
                    <IconButton size="small" onClick={() => handleRemoveRow(idx)} disabled={loading}>
                      <RemoveItemIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Paper>
              ))}
            </Stack>

            {assignmentRows.length > 0 && (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                <Typography variant="body2" color={assignmentTotalMatchesExpense ? 'success.main' : 'error.main'}>
                  Súčet: {app.formatNumber(assignmentTotal, 2)} € / {app.formatNumber(parsedAmount, 2)} €
                </Typography>
                {assignmentTotalMatchesExpense && <Chip label="OK" color="success" size="small" />}
              </Stack>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Zrušiť
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || assignmentRows.length === 0 || !assignmentTotalMatchesExpense}
        >
          {loading ? 'Ukladám...' : 'Vytvoriť a priradiť'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
